// src/pages/AnalyticsPage.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';


import { UserContext } from '../../App';

// Base API URL
const API_URL = process.env.REACT_APP_API_URL;

// Custom icons to match the theme
const Icons = {
    TrendingUp: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
        </svg>
    ),
    DollarSign: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    ),
    ShoppingBag: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
    )
};

const AnalyticsPage = () => {
    // Replace with a real supplier ID from your users table (user_type_id = 3)
    const { user } = useContext(UserContext);
    const accessToken = localStorage.getItem('accessToken');

    const [salesData, setSalesData] = useState([]);
    const [topSelling, setTopSelling] = useState([]);
    const [forecastData, setForecastData] = useState([]);
    const [summary, setSummary] = useState({ totalSales: 0, totalProductsSold: 0 });

    // Helper function to create headers with the auth token
    const createHeaders = () => ({
        'Authorization': `Bearer ${accessToken}`
    });

    // Fetches the summary of total sales and products sold for the supplier
    const fetchSummaryData = async () => {
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/analytics/summary`, {
                headers: createHeaders()
            });
            const data = await response.json();

            console.log("Summary API Response:", data);

            // Convert string values to numbers
            const summaryWithNumbers = {
                totalSales: Number(data.totalSales) || 0,
                totalProductsSold: Number(data.totalProductsSold) || 0,
            };
            setSummary(summaryWithNumbers);

        } catch (error) {
            console.error("Failed to fetch summary data:", error);
        }
    };

    // Fetches the sales trends data for the supplier
    const fetchSalesTrends = async () => {
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/analytics/sales-trends`, {
                headers: createHeaders()
            });
            const data = await response.json();

            console.log("Sales Trends API Response:", data);

            // Convert the sales amount to a number
            const salesDataWithNumbers = data.sales.map(item => ({
                ...item,
                totalSalesAmount: Number(item.totalSalesAmount)
            }));
            setSalesData(salesDataWithNumbers);
        } catch (error) {
            console.error("Failed to fetch sales trends:", error);
        }
    };

    // Fetches the top-selling products for the supplier
    const fetchTopSellingProducts = async () => {
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/analytics/top-selling-products`, {
                headers: createHeaders()
            });
            const data = await response.json();

            console.log("Top Selling Products API Response:", data);
            setTopSelling(data.products);
        } catch (error) {
            console.error("Failed to fetch top selling products:", error);
        }
    };

    // Fetches the sales forecast data for the supplier
    const fetchSalesForecast = async () => {
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/analytics/forecast`, {
                headers: createHeaders()
            });
            const data = await response.json();

            console.log("Sales Forecast API Response:", data);
            setForecastData(data.forecast);
        } catch (error) {
            console.error("Failed to fetch forecast:", error);
        }
    };

    // The useEffect hook with an empty dependency array
    // This will run the fetch functions only once when the component mounts
    useEffect(() => {
        fetchSummaryData();
        fetchSalesTrends();
        fetchTopSellingProducts();
        fetchSalesForecast();
    }, []);

    // Log the state variables whenever they are updated

    if (!user) {
        return <div className="min-h-screen bg-gray-100 font-sans p-8 text-center text-gray-500">Please log in to view this page.</div>;
    }

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-7xl mx-auto">
                <header className="mb-8">
                    <h1 className="text-4xl font-extrabold text-gray-900">
                        Analyse et Prévisions 📊
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Obtenez un aperçu des performances de votre entreprise et anticipez les tendances futures.
                    </p>
                </header>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                        <Icons.DollarSign className="w-12 h-12 text-indigo-500" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-700">Chiffre d'Affaires Total</h2>
                            <p className="text-3xl font-bold text-gray-900 mt-1">
                                {summary.totalSales.toFixed(2)} CDF
                            </p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                        <Icons.ShoppingBag className="w-12 h-12 text-green-500" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-700">Total Produits Vendus</h2>
                            <p className="text-3xl font-bold text-gray-900 mt-1">
                                {summary.totalProductsSold}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Sales Trend Chart */}
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Tendances des Ventes (Derniers 30 Jours)</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={salesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="logDate" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="totalSalesAmount" stroke="#8884d8" name="Chiffre d'affaires" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Sales Forecast Chart */}
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Prévisions des Ventes (Prochains 7 Jours)</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={forecastData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="sales" stroke="#82ca9d" name="Ventes Prévues" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Selling Products */}
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Top 5 des Produits les Plus Vendus</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
                                    <th className="py-3 px-6 text-left rounded-tl-xl">Nom du Produit</th>
                                    <th className="py-3 px-6 text-right">Quantité Vendue</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                {topSelling.length > 0 ? (
                                    topSelling.map((product, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-3 px-6 text-left">{product.productName}</td>
                                            <td className="py-3 px-6 text-right font-medium">{product.totalSold}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="py-10 text-center text-gray-500">
                                            Aucune donnée de vente disponible.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsPage;
